import { FlatList, Pressable, StyleSheet, View, type ListRenderItemInfo } from 'react-native';
import { CheckCircle, Search } from 'lucide-react-native';
import type { PhotoSearchMatchViewModel } from '../../types/photoSearch';
import { tokens } from '../../theme/tokens';
import { AppButton } from '../ui/AppButton';
import { AppCard } from '../ui/AppCard';
import { AppText } from '../ui/AppText';

type PhotoSearchRecognitionConfirmCardProps = {
  matches: PhotoSearchMatchViewModel[];
  selectedMatchId: string | null;
  isSubmitting: boolean;
  allowSkip?: boolean;
  helperText?: string;
  onSelectMatch: (match: PhotoSearchMatchViewModel) => void;
  onSkipMatch: () => void;
};

export function PhotoSearchRecognitionConfirmCard({
  matches,
  selectedMatchId,
  isSubmitting,
  allowSkip = true,
  helperText = '找到可能匹配的题目，确认后再进入 AI 讲解。',
  onSelectMatch,
  onSkipMatch,
}: PhotoSearchRecognitionConfirmCardProps) {
  const renderMatch = ({ item }: ListRenderItemInfo<PhotoSearchMatchViewModel>) => (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`选择识别顺序第 ${item.detectionOrder} 道题`}
      accessibilityState={{ selected: item.id === selectedMatchId }}
      onPress={() => onSelectMatch(item)}
      disabled={isSubmitting}
      style={({ pressed }) => [
        styles.matchCard,
        item.id === selectedMatchId ? styles.selectedMatchCard : null,
        pressed && !isSubmitting ? styles.pressed : null,
        isSubmitting ? styles.disabled : null,
      ]}
    >
      <View style={styles.matchHeader}>
        <View style={styles.matchTitleWrap}>
          <AppText variant="body" weight="bold">
            {item.questionNumber
              ? `第 ${item.questionNumber} 题 · ${item.title}`
              : `识别顺序 ${item.detectionOrder} · ${item.title}`}
          </AppText>
          <AppText variant="caption" tone="muted">
            {item.subject ?? '综合'} · {item.knowledgePoints[0] ?? item.sourceLabel}
          </AppText>
        </View>
        <View style={styles.confidencePill}>
          <CheckCircle size={14} color={tokens.colors.primary} />
          <AppText variant="caption" tone="primary" weight="bold">
            {item.confidencePercent}%
          </AppText>
        </View>
      </View>

      <AppText variant="caption" tone="muted" numberOfLines={2}>
        {item.ocrText || item.normalizedProblem || item.description}
      </AppText>
      <AppText variant="caption" tone="muted">
        {item.bounds
          ? `区域 x=${item.bounds.x}, y=${item.bounds.y}, ${item.bounds.width}×${item.bounds.height}`
          : '区域坐标未返回'}
      </AppText>
    </Pressable>
  );

  return (
    <AppCard style={styles.card}>
      <View style={styles.titleRow}>
        <View style={styles.titleIcon}>
          <Search size={18} color={tokens.colors.primary} />
        </View>
        <View style={styles.titleText}>
          <AppText variant="subtitle" weight="bold">
            确认识别结果
          </AppText>
          <AppText variant="caption" tone="muted">
            {helperText}
          </AppText>
        </View>
      </View>

      <FlatList
        data={matches}
        keyExtractor={(match) => match.id}
        renderItem={renderMatch}
        scrollEnabled={false}
        ItemSeparatorComponent={MatchSeparator}
      />

      {allowSkip !== false ? (
        <AppButton
          label="不选择，直接 AI 讲解"
          variant="secondary"
          onPress={onSkipMatch}
          loading={isSubmitting}
        />
      ) : null}
    </AppCard>
  );
}

function MatchSeparator() {
  return <View style={styles.separator} />;
}

const styles = StyleSheet.create({
  card: {
    gap: tokens.spacing.lg,
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  titleIcon: {
    width: tokens.layout.iconSize,
    height: tokens.layout.iconSize,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  titleText: {
    flex: 1,
  },
  matchCard: {
    borderRadius: tokens.radius.md,
    borderWidth: tokens.border.width,
    borderColor: tokens.colors.border,
    backgroundColor: tokens.colors.surface,
    padding: tokens.spacing.md,
    gap: tokens.spacing.sm,
  },
  selectedMatchCard: {
    borderColor: tokens.colors.primary,
    backgroundColor: tokens.colors.surfaceMuted,
  },
  pressed: {
    opacity: 0.86,
  },
  disabled: {
    opacity: 0.56,
  },
  matchHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacing.md,
  },
  matchTitleWrap: {
    flex: 1,
  },
  confidencePill: {
    minHeight: 28,
    borderRadius: tokens.radius.full,
    backgroundColor: tokens.colors.surfaceMuted,
    paddingHorizontal: tokens.spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.xs,
  },
  separator: {
    height: tokens.spacing.md,
  },
});
